import { useId, useState, type FormEvent } from "react";
import { Link } from "react-router";
import { useTranslation } from "react-i18next";
import { defaultSource, defaultTagMeansReleased, useAppMutations, useApps, useIgnoredTags } from "@/api/apps";
import { useGitlabEnvironments, useProjectGitlabRepos } from "@/api/hooks";
import type { App } from "@/api/types/App";
import type { AppPlatform } from "@/api/types/AppPlatform";
import type { Project } from "@/api/types/Project";
import type { UpdateAppRequest } from "@/api/types/UpdateAppRequest";
import type { VersionSource } from "@/api/types/VersionSource";
import { ConfirmDialog, Dialog } from "@/components/Dialog";
import { MoreMenu, type MoreItem } from "@/components/MoreMenu";
import { useToast } from "@/components/Toast";
import { Button, Checkbox, Chip, Field, Input, LoadError, Notice, Select, Spinner } from "@/components/ui";
import { APP_PLATFORMS } from "@/lib/testing";

const SOURCES: VersionSource[] = ["manual", "tag", "deployment"];

/** A project's apps and where their versions come from (project managers). */
export function AppsSection({ project }: { project: Project }) {
  const { t } = useTranslation();
  const apps = useApps(project.key, { includeArchived: true });
  const [editing, setEditing] = useState<App | "new" | null>(null);

  return (
    <section className="card flex flex-col gap-4 p-5">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <h3 className="m-0 label-caps">{t("apps.title")}</h3>
        {!project.archived && (
          <Button size="sm" icon="plus" onClick={() => setEditing("new")}>
            {t("apps.add")}
          </Button>
        )}
      </div>
      <p className="m-0 text-sm text-muted">{t("apps.intro")}</p>
      {apps.isPending ? (
        <Spinner />
      ) : apps.isError ? (
        <LoadError onRetry={() => void apps.refetch()} />
      ) : apps.data.length === 0 ? (
        <p className="m-0 text-sm text-muted">{t("apps.empty")}</p>
      ) : (
        <ul className="m-0 flex list-none flex-col gap-2.5 p-0">
          {apps.data.map((app) => (
            <li key={app.id}>
              <AppRow project={project} app={app} apps={apps.data} onEdit={() => setEditing(app)} />
            </li>
          ))}
        </ul>
      )}
      {editing && (
        <AppDialog project={project} app={editing === "new" ? null : editing} apps={apps.data ?? []} onClose={() => setEditing(null)} />
      )}
    </section>
  );
}

function AppRow({ project, app, apps, onEdit }: { project: Project; app: App; apps: App[]; onEdit: () => void }) {
  const { t } = useTranslation();
  const toast = useToast();
  const { update, remove } = useAppMutations(project.key);
  const [confirm, setConfirm] = useState<"archive" | "delete" | "merge" | null>(null);
  const mergeTargets = apps.filter((other) => other.id !== app.id && !other.archived);

  const save = (body: Partial<UpdateAppRequest>, message: string) =>
    update.mutate(
      { id: app.id, body: { ...EMPTY_UPDATE, ...body } },
      {
        onSuccess: () => {
          setConfirm(null);
          toast.success(message);
        },
        onError: (e) => toast.error(e),
      },
    );

  const items: MoreItem[] = project.archived
    ? []
    : [
        { label: t("common.edit"), icon: "edit", onSelect: onEdit },
        ...(app.needs_sorting
          ? [{ label: t("apps.markSorted"), icon: "check" as const, onSelect: () => save({ needs_sorting: false }, t("apps.saved")) }]
          : []),
        app.archived
          ? { label: t("apps.unarchive"), icon: "archive" as const, onSelect: () => save({ archived: false }, t("apps.unarchived")) }
          : { label: t("apps.archive"), icon: "archive" as const, onSelect: () => setConfirm("archive") },
        ...(mergeTargets.length > 0 ? [{ label: t("apps.merge"), icon: "merge" as const, onSelect: () => setConfirm("merge") }] : []),
        ...(app.in_use ? [] : [{ label: t("common.delete"), icon: "trash" as const, danger: true, onSelect: () => setConfirm("delete") }]),
      ];

  return (
    <div className="flex flex-col gap-2 rounded-[var(--radius-ctl)] border border-border p-3.5">
      <div className="flex flex-wrap items-center gap-2">
        <span className="min-w-0 flex-1 basis-40">
          <span className="block truncate font-semibold">{app.name}</span>
          <span className="block text-xs text-muted">
            {t(`apps.sources.${app.source}`)} · {t("apps.versionCount", { count: app.version_count })}
          </span>
        </span>
        {app.needs_sorting && <Chip tone="blocked">{t("apps.needsSorting")}</Chip>}
        {app.archived && <Chip tone="outline">{t("apps.archived")}</Chip>}
        {app.platforms.map((p) => (
          <Chip key={p}>{t(`apps.platforms.${p}`)}</Chip>
        ))}
        <MoreMenu items={items} />
      </div>

      {app.repo && (
        <p className="m-0 font-mono text-xs break-all text-muted">
          {app.repo.path}
          {app.source === "tag" && ` · ${t("apps.tagPattern", { prefix: app.tag_prefix })}`}
          {app.environment && ` · ${app.environment}`}
        </p>
      )}
      {app.source === "tag" && !app.archived && (
        <p className="m-0 text-xs text-muted">{t(app.tag_means_released ? "apps.tagReleases" : "apps.tagNotReleases")}</p>
      )}
      {app.needs_sorting && <p className="m-0 text-xs text-muted">{t("apps.needsSortingHint")}</p>}
      {app.platforms.length === 0 && !app.archived && <Notice tone="blocked">{t("apps.noPlatforms")}</Notice>}
      {app.repo && !app.repo.linked && <Notice tone="blocked">{t("apps.repoRemoved")}</Notice>}
      {app.shared_with.length > 0 && <Notice>{t(sharedKey(app.source), { apps: app.shared_with.join(", ") })}</Notice>}
      {app.source === "tag" && <IgnoredTags project={project} app={app} />}

      <ConfirmDialog
        open={confirm === "archive"}
        onClose={() => setConfirm(null)}
        title={t("apps.archiveTitle", { name: app.name })}
        body={t("apps.archiveBody")}
        confirmLabel={t("apps.archive")}
        loading={update.isPending}
        onConfirm={() => save({ archived: true }, t("apps.archivedToast"))}
      />
      <ConfirmDialog
        open={confirm === "delete"}
        onClose={() => setConfirm(null)}
        title={t("apps.deleteTitle", { name: app.name })}
        body={t("apps.deleteBody")}
        confirmLabel={t("common.delete")}
        danger
        loading={remove.isPending}
        onConfirm={() =>
          remove.mutate(app.id, {
            onSuccess: () => {
              setConfirm(null);
              toast.success(t("apps.deleted"));
            },
            onError: (e) => toast.error(e),
          })
        }
      />
      {confirm === "merge" && <MergeDialog project={project} app={app} targets={mergeTargets} onClose={() => setConfirm(null)} />}
    </div>
  );
}

function MergeDialog({ project, app, targets, onClose }: { project: Project; app: App; targets: App[]; onClose: () => void }) {
  const { t } = useTranslation();
  const toast = useToast();
  const { merge } = useAppMutations(project.key);
  const [into, setInto] = useState(targets[0]?.id ?? "");

  return (
    <Dialog
      open
      onClose={onClose}
      title={t("apps.mergeTitle", { name: app.name })}
      width={480}
      footer={
        <>
          <Button onClick={onClose}>{t("common.cancel")}</Button>
          <Button
            variant="danger"
            loading={merge.isPending}
            disabled={!into}
            onClick={() =>
              merge.mutate(
                { id: app.id, into },
                {
                  onSuccess: () => {
                    toast.success(t("apps.merged"));
                    onClose();
                  },
                  onError: (e) => toast.error(e),
                },
              )
            }
          >
            {t("apps.mergeConfirm")}
          </Button>
        </>
      }
    >
      <div className="flex flex-col gap-4">
        <p className="m-0 text-sm text-text-2">{t("apps.mergeBody", { name: app.name })}</p>
        <Field label={t("apps.mergeInto")}>
          {(p) => (
            <Select {...p} value={into} onChange={(e) => setInto(e.target.value)}>
              {targets.map((other) => (
                <option key={other.id} value={other.id}>
                  {other.name}
                </option>
              ))}
            </Select>
          )}
        </Field>
      </div>
    </Dialog>
  );
}

const EMPTY_UPDATE: UpdateAppRequest = {
  name: null,
  platforms: null,
  source: null,
  repo_id: null,
  repo_path: null,
  tag_prefix: null,
  tag_means_released: null,
  environment: null,
  archived: null,
  needs_sorting: null,
};

function sharedKey(source: VersionSource) {
  return source === "tag" ? "apps.sharedPrefix" : "apps.sharedEnvironment";
}

function IgnoredTags({ project, app }: { project: Project; app: App }) {
  const { t } = useTranslation();
  const tags = useIgnoredTags(project.key, app.id);
  if (!tags.data?.length) return null;
  return (
    <details className="text-xs text-muted">
      <summary className="cursor-pointer">{t("apps.ignoredTags", { count: tags.data.length })}</summary>
      <ul className="m-0 mt-1.5 flex list-none flex-col gap-1 p-0">
        {tags.data.map((tag) => (
          <li key={tag.name} className="break-all">
            <span className="font-mono">{tag.name}</span>: {t(`apps.ignoredReason.${tag.reason}`)}
          </li>
        ))}
      </ul>
    </details>
  );
}

function AppDialog({ project, app, apps, onClose }: { project: Project; app: App | null; apps: App[]; onClose: () => void }) {
  const { t } = useTranslation();
  const toast = useToast();
  const { create, update } = useAppMutations(project.key);
  const repos = useProjectGitlabRepos(project.key);
  const environments = useGitlabEnvironments(project.key);
  const listId = useId();

  const [name, setName] = useState(app?.name ?? "");
  const [platforms, setPlatforms] = useState<AppPlatform[]>(app?.platforms ?? []);
  // Untouched choices follow the platforms, so the defaults show before saving.
  const [chosenSource, setChosenSource] = useState<VersionSource | null>(app?.source ?? null);
  const [chosenRepo, setChosenRepo] = useState(app?.repo?.id ?? "");
  const [prefix, setPrefix] = useState(app?.tag_prefix ?? "");
  const [chosenReleased, setChosenReleased] = useState<boolean | null>(app?.source === "tag" ? app.tag_means_released : null);
  const [environment, setEnvironment] = useState(app?.environment ?? "");
  const [tried, setTried] = useState(false);

  const linked = repos.data?.repos ?? [];
  // Nothing to deploy from yet, so the default for web and backend apps couldn't be saved.
  const source = chosenSource ?? (linked.length > 0 ? defaultSource(platforms) : "manual");
  const released = chosenReleased ?? defaultTagMeansReleased(platforms);
  const usesRepo = source !== "manual";
  const repoId = chosenRepo || (linked.length === 1 ? (linked[0]?.id ?? "") : "");
  const env = environment.trim();
  const tagPrefix = prefix.trim();
  const needsPlatforms = platforms.length === 0 && (!app || app.platforms.length > 0);
  const valid = !!name.trim() && !needsPlatforms && (!usesRepo || !!repoId) && (source !== "deployment" || !!env);
  const busy = create.isPending || update.isPending;
  const removedRepo = repos.data && app?.repo && !linked.some((r) => r.id === app.repo?.id) ? app.repo : null;
  // The notice in the form says why, and there's no repo field to show an error on.
  const noRepoToPick = usesRepo && linked.length === 0 && !removedRepo;

  const shared = !usesRepo
    ? []
    : apps
        .filter(
          (other) =>
            other.id !== app?.id &&
            !other.archived &&
            other.source === source &&
            other.repo?.id === repoId &&
            (source === "tag" ? other.tag_prefix === tagPrefix : other.environment === env),
        )
        .map((other) => other.name);

  const togglePlatform = (p: AppPlatform) => setPlatforms((cur) => (cur.includes(p) ? cur.filter((x) => x !== p) : [...cur, p]));

  const submit = (e: FormEvent) => {
    e.preventDefault();
    setTried(true);
    if (!valid) return;
    const done = {
      onSuccess: () => {
        toast.success(t(app ? "apps.saved" : "apps.created"));
        onClose();
      },
      onError: (err: unknown) => toast.error(err),
    };
    const fields = {
      name: name.trim(),
      platforms,
      source,
      repo_id: usesRepo ? repoId : null,
      repo_path: null,
      tag_prefix: source === "tag" ? tagPrefix : null,
      tag_means_released: source === "tag" ? released : null,
      environment: source === "deployment" ? env : null,
    };
    if (app) update.mutate({ id: app.id, body: { ...EMPTY_UPDATE, ...fields } }, done);
    else create.mutate(fields, done);
  };

  return (
    <Dialog
      open
      onClose={onClose}
      title={app ? t("apps.edit") : t("apps.add")}
      width={520}
      footer={
        <>
          <Button onClick={onClose}>{t("common.cancel")}</Button>
          <Button variant="primary" type="submit" form="app-form" loading={busy} disabled={noRepoToPick}>
            {app ? t("common.save") : t("apps.add")}
          </Button>
        </>
      }
    >
      <form id="app-form" onSubmit={submit} className="flex flex-col gap-4" noValidate>
        <Field label={t("apps.name")} error={tried && !name.trim() ? t("apps.required") : undefined}>
          {(p) => <Input {...p} autoFocus value={name} maxLength={app && app.name.length > 80 ? undefined : 80} onChange={(e) => setName(e.target.value)} />}
        </Field>
        <fieldset className="m-0 flex flex-col gap-1.5 border-0 p-0">
          <legend className="mb-1.5 p-0 text-sm font-medium text-text-2">{t("apps.platformsLabel")}</legend>
          <div className="flex flex-wrap gap-x-4 gap-y-2">
            {APP_PLATFORMS.map((p) => (
              <Checkbox key={p} label={t(`apps.platforms.${p}`)} checked={platforms.includes(p)} onChange={() => togglePlatform(p)} />
            ))}
          </div>
          {tried && needsPlatforms && <p className="m-0 text-xs text-fail-fg">{t("apps.platformsRequired")}</p>}
        </fieldset>
        <Field label={t("apps.source")} hint={app ? t("apps.sourceHintEdit") : t(`apps.sourceHints.${source}`)}>
          {(p) => (
            <Select {...p} value={source} onChange={(e) => setChosenSource(e.target.value as VersionSource)}>
              {SOURCES.map((s) => (
                <option key={s} value={s}>
                  {t(`apps.sources.${s}`)}
                </option>
              ))}
            </Select>
          )}
        </Field>
        {usesRepo &&
          (noRepoToPick ? (
            repos.isPending ? (
              <Spinner />
            ) : repos.isError ? (
              <LoadError onRetry={() => void repos.refetch()} />
            ) : (
              <Notice tone="blocked">
                {t("apps.noRepos")} <Link to={`/p/${project.key}/settings/gitlab`}>{t("apps.openGitlab")}</Link>
              </Notice>
            )
          ) : (
            <Field label={t("apps.repo")} error={tried && !repoId ? t("apps.required") : undefined}>
              {(p) => (
                <Select {...p} value={repoId} onChange={(e) => setChosenRepo(e.target.value)}>
                  <option value="" disabled>
                    {t("apps.pickRepo")}
                  </option>
                  {removedRepo && <option value={removedRepo.id}>{t("apps.removedRepoOption", { path: removedRepo.path })}</option>}
                  {linked.map((r) => (
                    <option key={r.id} value={r.id}>
                      {r.path}
                    </option>
                  ))}
                </Select>
              )}
            </Field>
          ))}
        {source === "tag" && (
          <>
            <Field label={t("apps.tagPrefix")} optional hint={t("apps.tagPrefixHint")}>
              {(p) => <Input {...p} className="font-mono" autoComplete="off" maxLength={100} value={prefix} onChange={(e) => setPrefix(e.target.value)} />}
            </Field>
            <Checkbox label={t("apps.tagMeansReleased")} checked={released} onChange={(e) => setChosenReleased(e.target.checked)} />
          </>
        )}
        {source === "deployment" && (
          <Field label={t("apps.environment")} error={tried && !env ? t("apps.required") : undefined} hint={t("apps.environmentHint")}>
            {(p) => (
              <>
                <Input {...p} list={listId} autoComplete="off" maxLength={255} value={environment} onChange={(e) => setEnvironment(e.target.value)} />
                <datalist id={listId}>
                  {(environments.data ?? []).map((n) => (
                    <option key={n} value={n} />
                  ))}
                </datalist>
              </>
            )}
          </Field>
        )}
        {shared.length > 0 && <Notice>{t(sharedKey(source), { apps: shared.join(", ") })}</Notice>}
      </form>
    </Dialog>
  );
}
